import React, { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { 
  FaUsers, 
  FaProjectDiagram, 
  FaUserCheck, 
  FaUserTimes,
  FaChartLine,
  FaCalendarAlt,
  FaBell,
  FaUserPlus,
  FaEdit,
  FaDownload,
  FaEye,
  FaExclamationTriangle,
  FaCheckCircle,
  FaClock
} from 'react-icons/fa';
import IconWrapper from '../common/IconWrapper';

const DashboardContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  color: #141340;
  font-size: 2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  
  &::after {
    content: '';
    flex: 1;
    height: 2px;
    background: linear-gradient(90deg, #F68B21 0%, transparent 100%);
  }
`;

const PageSubtitle = styled.p`
  color: #666;
  font-size: 1.1rem;
  margin-bottom: 2rem;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #F68B21 0%, #e67c1a 100%);
  }
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  }
`;

const StatHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`;

const StatIcon = styled.div<{ color: string }>`
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: ${({ color }) => color};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  box-shadow: 0 4px 12px ${({ color }) => color}40;
`;

const StatChange = styled.span<{ $positive: boolean }>`
  font-size: 0.9rem;
  font-weight: 600;
  color: ${({ $positive }) => ($positive ? '#28a745' : '#dc3545')};
  background: ${({ $positive }) => ($positive ? 'rgba(40, 167, 69, 0.1)' : 'rgba(220, 53, 69, 0.1)')};
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
`;

const StatValue = styled.div`
  font-size: 2.5rem;
  font-weight: 700;
  color: #141340;
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 1rem;
  color: #666;
  font-weight: 500;
`;

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const MainSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Section = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  border: 1px solid #e0e0e0;
`;

const SectionTitle = styled.h3`
  color: #141340;
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: linear-gradient(90deg, #F68B21 0%, transparent 100%);
  }
`;

const RecentActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ActivityItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 1rem;
  border-radius: 8px;
  background: #f8f9fa;
  transition: all 0.3s ease;
  
  &:hover {
    background: #e9ecef;
    transform: translateX(4px);
  }
`;

const ActivityIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1rem;
  flex-shrink: 0;
`;

const ActivityContent = styled.div`
  flex: 1;
`;

const ActivityTitle = styled.div`
  font-weight: 600;
  color: #141340;
  margin-bottom: 0.25rem;
`;

const ActivityTime = styled.div`
  font-size: 0.8rem;
  color: #666;
`;

const QuickActions = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
`;

const ActionButton = styled(Link)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 1.5rem 1rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  text-decoration: none;
  color: #141340;
  transition: all 0.3s ease;
  text-align: center;
  
  &:hover {
    background: linear-gradient(135deg, #F68B21 0%, #e67c1a 100%);
    color: white;
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(246, 139, 33, 0.3);
    border-color: #F68B21;
  }
  
  .action-icon {
    font-size: 1.5rem;
    color: #F68B21;
    transition: all 0.3s ease;
  }
  
  &:hover .action-icon {
    color: white;
  }
  
  .action-text {
    font-weight: 600;
    font-size: 0.9rem;
  }
`;

const HRDashboard: React.FC = () => {
  // Mock data for HR dashboard
  const stats = [
    {
      title: 'Total Staff',
      value: '24',
      change: '+3',
      positive: true,
      icon: <IconWrapper icon={FaUsers} />,
      color: '#F68B21'
    },
    {
      title: 'Active Projects',
      value: '8',
      change: '+1',
      positive: true,
      icon: <IconWrapper icon={FaProjectDiagram} />,
      color: '#28a745'
    },
    {
      title: 'Staff on Leave',
      value: '2',
      change: '-1',
      positive: true,
      icon: <IconWrapper icon={FaUserTimes} />,
      color: '#dc3545'
    },
    {
      title: 'New Hires',
      value: '3',
      change: '+2',
      positive: true,
      icon: <IconWrapper icon={FaUserCheck} />,
      color: '#17a2b8'
    }
  ];

  const recentActivities = [
    {
      title: 'New staff member added',
      description: 'John Doe joined as Project Coordinator',
      time: '2 hours ago',
      icon: <IconWrapper icon={FaUsers} />
    },
    {
      title: 'Leave request approved',
      description: 'Sarah Johnson - Annual leave approved',
      time: '4 hours ago',
      icon: <IconWrapper icon={FaCalendarAlt} />
    },
    {
      title: 'Performance review scheduled',
      description: 'Monthly review for Development team',
      time: '1 day ago',
      icon: <IconWrapper icon={FaChartLine} />
    },
    {
      title: 'Training notification sent',
      description: 'Safety training reminder sent to all staff',
      time: '2 days ago',
      icon: <IconWrapper icon={FaBell} />
    }
  ];

  return (
    <DashboardContainer>
      <PageTitle>
        <IconWrapper icon={FaUsers} />
        HR Dashboard
      </PageTitle>
      <PageSubtitle>
        Welcome to the HR (Admin) Portal. Manage staff, view projects, and access HR-specific tools.
      </PageSubtitle>

      <StatsGrid>
        {stats.map((stat, index) => (
          <StatCard key={index}>
            <StatHeader>
              <StatIcon color={stat.color}>
                {stat.icon}
              </StatIcon>
              <StatChange $positive={stat.positive}>
                {stat.change}
              </StatChange>
            </StatHeader>
            <StatValue>{stat.value}</StatValue>
            <StatLabel>{stat.title}</StatLabel>
          </StatCard>
        ))}
      </StatsGrid>

      <ContentGrid>
        <MainSection>
          <Section>
            <SectionTitle>
              <IconWrapper icon={FaBell} />
              Recent Activities
            </SectionTitle>
            <RecentActivityList>
              {recentActivities.map((activity, index) => (
                <ActivityItem key={index}>
                  <ActivityIcon>
                    {activity.icon}
                  </ActivityIcon>
                  <ActivityContent>
                    <ActivityTitle>{activity.title}</ActivityTitle>
                    <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.25rem' }}>
                      {activity.description}
    </div>
                    <ActivityTime>{activity.time}</ActivityTime>
                  </ActivityContent>
                </ActivityItem>
              ))}
            </RecentActivityList>
          </Section>
        </MainSection>

        <Section>
          <SectionTitle>
            <IconWrapper icon={FaUsers} />
            Quick Actions
          </SectionTitle>
          <QuickActions>
            <ActionButton to="/hr/staff">
              <IconWrapper icon={FaUserPlus} className="action-icon" />
              <span className="action-text">Add Staff</span>
            </ActionButton>
            <ActionButton to="/hr/projects">
              <IconWrapper icon={FaProjectDiagram} className="action-icon" />
              <span className="action-text">View Projects</span>
            </ActionButton>
            <ActionButton to="/hr/staff">
              <IconWrapper icon={FaEdit} className="action-icon" />
              <span className="action-text">Manage Staff</span>
            </ActionButton>
            <ActionButton to="/hr/staff">
              <IconWrapper icon={FaUsers} className="action-icon" />
              <span className="action-text">Assign to Projects</span>
            </ActionButton>
            <ActionButton to="/hr/staff">
              <IconWrapper icon={FaCalendarAlt} className="action-icon" />
              <span className="action-text">Leave Requests</span>
            </ActionButton>
            <ActionButton to="/hr/staff">
              <IconWrapper icon={FaDownload} className="action-icon" />
              <span className="action-text">Export Reports</span>
            </ActionButton>
          </QuickActions>
        </Section>
      </ContentGrid>
    </DashboardContainer>
  );
};

export default HRDashboard; 